import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, Coins, Copy, Plus, MagnifyingGlass, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { Logo } from "@/components/Logo";

export default function AdminPanel() {
  const t = useT();
  const nav = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ email: "", name: "", tokens: 100, manual: false, password: "" });
  const [creating, setCreating] = useState(false);
  const [lastPw, setLastPw] = useState(null);
  const [edits, setEdits] = useState({});

  const load = useCallback(async (search) => {
    try {
      const { data } = await api.get("/admin/users", { params: search ? { q: search } : {} });
      setUsers(data);
    } catch (e) {
      console.error("admin users load failed:", e);
    }
  }, []);

  useEffect(() => { load(""); }, [load]);
  useEffect(() => {
    const id = setTimeout(() => load(q), 300);
    return () => clearTimeout(id);
  }, [q, load]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const err = (e) => {
    const d = e.response?.data?.detail;
    toast.error(typeof d === "string" ? t(d) : t("admin.error"));
  };

  const createUser = async () => {
    if (!form.email.trim()) { toast.error(t("err.email_required")); return; }
    setCreating(true); setLastPw(null);
    try {
      const { data } = await api.post("/admin/users", {
        email: form.email, name: form.name || undefined,
        tokens: Number(form.tokens) || 0,
        password: form.manual ? form.password : undefined,
      });
      toast.success(t("admin.created"));
      if (data.generated_password) setLastPw({ email: data.email, password: data.generated_password });
      setForm({ email: "", name: "", tokens: 100, manual: false, password: "" });
      load(q);
    } catch (e) { err(e); } finally { setCreating(false); }
  };

  const saveTokens = async (u) => {
    const val = edits[u.id];
    if (val === undefined || val === "" || Number(val) === u.tokens_balance) return;
    try {
      await api.patch(`/admin/users/${u.id}`, { set: Number(val) });
      toast.success(t("admin.updated"));
      setEdits((s) => { const n = { ...s }; delete n[u.id]; return n; });
      load(q);
    } catch (e) { err(e); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(t("admin.confirmDelete").replace("{email}", u.email))) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(t("admin.deleted"));
      load(q);
    } catch (e) { err(e); }
  };

  const sortedUsers = [...users].sort((a, b) => {
    const aAdmin = a.role === "admin", bAdmin = b.role === "admin";
    if (aAdmin !== bAdmin) return aAdmin ? -1 : 1;
    return (a.email || "").localeCompare(b.email || "");
  });

  const copyPw = async () => {
    if (!lastPw) return;
    await copyToClipboard(lastPw.password);
    toast.success(t("admin.copied"));
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-extrabold text-xl text-foreground">
            <Logo size={24} className="text-brand-yellow" /> <span>{t("admin.link")}</span>
          </div>
          <Button variant="ghost" onClick={() => nav("/dashboard")} className="gap-2" data-testid="admin-back">
            <ArrowLeft size={18} /> {t("admin.back")}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {/* Create account */}
        <section className="bg-card border border-border rounded-xl p-6" data-testid="admin-create-section">
          <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><Plus size={20} className="text-gold" /> {t("admin.createTitle")}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="text-xs text-muted-foreground">{t("admin.email")}</label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nome@kihapapp.com" data-testid="admin-new-email" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t("admin.name")}</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-new-name" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t("admin.tokens")}</label>
              <Input type="number" min="0" value={form.tokens} onChange={(e) => setForm({ ...form, tokens: e.target.value })} style={{ colorScheme: "dark" }} data-testid="admin-new-tokens" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{t("admin.password")}</label>
              {form.manual ? (
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-new-password" />
              ) : (
                <div className="h-10 flex items-center text-sm text-muted-foreground">{t("admin.pwAutoHint")}</div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.manual} onChange={(e) => setForm({ ...form, manual: e.target.checked })} data-testid="admin-pw-manual-toggle" />
              {t("admin.pwManual")}
            </label>
            <Button onClick={createUser} disabled={creating} className="gap-2" data-testid="admin-create-btn">
              <Plus size={18} /> {t("admin.create")}
            </Button>
          </div>
          {lastPw && (
            <div className="mt-4 bg-secondary/50 border border-border rounded-md p-3 flex items-center justify-between gap-3" data-testid="admin-generated-pw">
              <div className="text-sm">
                <div className="text-muted-foreground">{t("admin.genPw")}</div>
                <div className="font-mono font-semibold">{lastPw.email} · {lastPw.password}</div>
              </div>
              <Button size="sm" variant="outline" onClick={copyPw} className="gap-2"><Copy size={16} /> {t("admin.copy")}</Button>
            </div>
          )}
        </section>

        {/* Users list */}
        <section className="bg-card border border-border rounded-xl p-6" data-testid="admin-users-section">
          <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
            <h2 className="font-heading font-bold text-lg">{t("admin.usersTitle")} · {users.length}</h2>
            <div className="relative w-full sm:w-72">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.search")} className="pl-9" data-testid="admin-search" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4">{t("admin.email")}</th>
                  <th className="py-2 pr-4">{t("admin.name")}</th>
                  <th className="py-2 pr-4">{t("admin.role")}</th>
                  <th className="py-2 pr-4">{t("admin.balance")}</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u) => (
                  <tr key={u.id} className={`border-b border-border/50 ${u.role === "admin" ? "text-gold bg-gold/5 font-semibold" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                    <td className="py-2 pr-4 font-mono">{u.email}</td>
                    <td className="py-2 pr-4">{u.name}</td>
                    <td className="py-2 pr-4">
                      {u.role === "admin" ? <span className="text-gold font-semibold">admin</span> : <span className="text-muted-foreground">organizer</span>}
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-1">
                        <Coins size={14} className="text-gold" />
                        <Input
                          type="number" min="0"
                          value={edits[u.id] ?? u.tokens_balance}
                          onChange={(e) => setEdits((s) => ({ ...s, [u.id]: e.target.value }))}
                          className="h-8 w-24"
                          style={{ colorScheme: "dark" }}
                          data-testid={`admin-tokens-input-${u.id}`}
                        />
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => saveTokens(u)} data-testid={`admin-save-${u.id}`}>{t("admin.save")}</Button>
                        {u.role !== "admin" && (
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-400" onClick={() => deleteUser(u)} data-testid={`admin-delete-${u.id}`} title={t("admin.delete")}><Trash size={16} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">{t("admin.noUsers")}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
